/** @file Keys an IK constraint in an animation (mix, softness, bendPositive, stretch, compress). */
import { z } from 'zod';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import type { Doc } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';
import type { CommandContext } from '../commandContext';
import { command as setKey } from '../animation/setKey.command';

/** ik.setKey channel → the animation.setKey channel that stores it. */
const CHANNEL = { mix: 'ikMix', softness: 'ikSoftness', bendPositive: 'ikBend', stretch: 'ikStretch', compress: 'ikCompress' } as const;

const inputSchema = z
  .object({
    animation: z.string().min(1),
    target: z.string().min(1),
    channel: z.enum(['mix', 'softness', 'bendPositive', 'stretch', 'compress']),
    frame: prim.frame,
    value: z.unknown(),
    curve: prim.curve.exactOptional(),
  })
  .strict();

/** True when this key is already stored: same value, and the same curve when one is given (a no-op, R2-13). */
function alreadyKeyed(doc: Doc, ctx: CommandContext, input: z.infer<typeof inputSchema>): boolean {
  const timelines = doc.animations[ctx.resolve('animation', input.animation)]?.ik[ctx.resolve('ik', input.target)];
  const keys: readonly { f: number; v: unknown; curve?: unknown }[] = timelines?.[input.channel] ?? [];
  const key = keys.find((k) => k.f === input.frame);
  if (key === undefined || key.v !== input.value) return false;
  return input.curve === undefined || JSON.stringify(key.curve) === JSON.stringify(input.curve);
}

/** ik.setKey: animation.setKey on the matching ik* channel (same storage, curve and value rules); a stored key is a no-op. */
export const command = defineCommand({
  name: 'ik.setKey',
  description:
    'Set a key of an IK constraint in an animation: channel mix (0..1) or softness (>= 0) with an optional curve, or bendPositive|stretch|compress (boolean, no curve); same storage as animation.setKey ik* channels.',
  input: inputSchema,
  apply(draft, input, ctx) {
    if (alreadyKeyed(draft, ctx, input)) return;
    setKey.apply(draft, { ...input, channel: CHANNEL[input.channel] }, ctx);
  },
});
